import React, {
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import useFormCrafterSchema from '../../../hooks/ui/formCrafter/useFormCrafterSchema';
import useFormDataManager from '../../../hooks/ui/formCrafter/useFormDataManager';
import useFormValidation from '../../../hooks/ui/formCrafter/useFormValidation';
import { convertToFormCrafterSchema } from '../../../utils/formCrafter/schemaUtils';
import { isFileType } from '../../../utils/formCrafter/fieldTypeUtils';
import useFileAttachments from '../../../hooks/ui/formCrafter/useFileAttachments';
import IDSSubformRenderer from './renderers/IDSSubformRenderer';
import './FormCrafterFormRenderer.css';

/**
 * FormCrafterFormRenderer - A common component for rendering forms using IDS components
 *
 * @param {number} formId - Form ID for nested data structure (required for multi-form submissions)
 * @param {Array} subforms - Array of subform objects with json_object and id
 * @param {Object} filledForm - Previously filled form data (optional)
 * @param {Function} handleSaveForm - Function to handle form submission
 * @param {Boolean} disabled - Whether the form is disabled (read-only)
 * @param {Boolean} showSubmitButton - Whether to show submit button (default: true when not disabled)
 * @param {number} activeSubformIndex - Index of the active subform to display (optional, shows all if not provided)
 * @param {Function} onValidationChange - Callback when validation state changes per subform
 * @param {Boolean} validateOnBlur - Enable validation on field blur (default: true)
 * @param {React.Ref} ref - Ref to expose validateAndGetData method
 */
const FormCrafterFormRenderer = forwardRef(
  (
    {
      formId,
      subforms = [],
      filledForm,
      handleSaveForm,
      disabled = false,
      showSubmitButton = null,
      activeSubformIndex = null,
      onValidationChange,
      validateOnBlur = true,
      // Props for eager file upload
      onStagedFileUpload,
      onStagedFileDelete,
      eagerUploadEnabled = false,
      // Draft staged files (pre-loaded from draft)
      draftStagedFiles = {},
      // Deleted file IDs from draft (sandbox behavior - files deleted in draft only)
      draftDeletedFileIds = [],
      onServerFileDeleteConfirmed,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const containerRefs = useRef({});

    // Track deleted server file IDs for submission
    const [deletedFileIds, setDeletedFileIds] = useState([]);

    // Add a file ID to the deletion tracking set
    const addDeletedFileId = useCallback((fileId) => {
      setDeletedFileIds((prev) => [...new Set([...prev, fileId])]);
    }, []);

    // Handle server file deletion
    const handleServerFileDelete = useCallback(
      async (fileId) => {
        addDeletedFileId(fileId);
        if (onServerFileDeleteConfirmed) {
          await onServerFileDeleteConfirmed(fileId);
        }
      },
      [addDeletedFileId, onServerFileDeleteConfirmed]
    );

    // Use shared schema hook for parsing
    const { schemas, formData: parsedFormData } = useFormCrafterSchema(
      subforms,
      filledForm,
      formId
    );

    // Combine local deletedFileIds (current session) with draftDeletedFileIds (from saved draft)
    // This ensures that files deleted in the draft are filtered out when loading
    const combinedDeletedFileIds = useMemo(() => {
      const combined = new Set([...deletedFileIds, ...draftDeletedFileIds]);
      return Array.from(combined);
    }, [deletedFileIds, draftDeletedFileIds]);

    // Use file attachments hook - pass combined deleted file IDs for filtering
    const { hasFilesForField, getFilesForField, serverFilesByField } =
      useFileAttachments(
        filledForm,
        subforms,
        {},
        draftStagedFiles,
        formId,
        combinedDeletedFileIds
      );

    // Use form data manager hook
    const { formDataBySubform, updateFieldValue, collectAllFormData } =
      useFormDataManager(subforms, parsedFormData, schemas, filledForm);

    // Use form validation hook
    const {
      errors,
      setErrors,
      touchedFields,
      touchField,
      touchAllSubformFields,
      validateOnFieldBlur,
      validateAllSubforms,
      revalidateErroredSubforms,
      scrollToFirstError,
      getValidationState,
      registerErrorRef,
    } = useFormValidation(schemas, hasFilesForField, onValidationChange);

    // Clear stale file-required errors after file upload/delete.
    // draftStagedFiles updates asynchronously after eager upload completes;
    // this effect re-validates only subforms that already have errors.
    const prevDraftStagedFilesRef = useRef(draftStagedFiles);
    useEffect(() => {
      if (prevDraftStagedFilesRef.current === draftStagedFiles) return;
      prevDraftStagedFilesRef.current = draftStagedFiles;
      revalidateErroredSubforms(subforms, formDataBySubform);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftStagedFiles]);

    // Handle single field change
    const handleFieldChange = useCallback(
      (subformId, fieldId, value) => {
        updateFieldValue(subformId, fieldId, value);

        // Validate on change if there are already touched fields
        if (validateOnBlur && touchedFields[subformId]) {
          const updatedFormData = {
            ...(formDataBySubform[subformId] || {}),
            [fieldId]: value,
          };
          validateOnFieldBlur(subformId, updatedFormData);
        }
      },
      [
        validateOnBlur,
        touchedFields,
        formDataBySubform,
        updateFieldValue,
        validateOnFieldBlur,
      ]
    );

    // Handle field blur for touch tracking
    const handleFieldBlur = useCallback(
      (subformId, fieldId) => {
        touchField(subformId, fieldId);

        if (validateOnBlur) {
          const formData = formDataBySubform[subformId] || {};
          validateOnFieldBlur(subformId, formData);
        }
      },
      [validateOnBlur, formDataBySubform, touchField, validateOnFieldBlur]
    );

    // Collect and validate all forms
    const collectAndValidateAllForms = useCallback(() => {
      if (disabled) return { isValid: false, data: null, errors: {} };

      const { isValid, errors: newErrors } = validateAllSubforms(
        subforms,
        formDataBySubform
      );
      const collectedData = collectAllFormData(subforms);

      // Augment file fields: form data clears file values to [] (files are
      // managed via useFileAttachments), but the backend validator needs
      // references to existing server/staged files so it doesn't reject
      // required file fields on send-back or draft submissions.
      subforms.forEach((subform) => {
        const schema = schemas[subform.id];
        if (!schema?.fields) return;
        schema.fields.forEach((field) => {
          if (!isFileType(field.type)) return;
          const currentValue = collectedData[subform.id]?.[field.id];
          const valueIsEmpty =
            !currentValue ||
            (Array.isArray(currentValue) && currentValue.length === 0);
          if (!valueIsEmpty) return;

          const existingFiles = getFilesForField(subform.id, field.id);
          if (existingFiles.length > 0) {
            if (!collectedData[subform.id]) collectedData[subform.id] = {};
            collectedData[subform.id][field.id] = existingFiles.map(
              (f) => f.name || f.filename || String(f.id)
            );
          }
        });
      });

      // Combine local deletions (this session) with draft deletions (from saved draft)
      // This ensures files deleted in draft mode are properly tracked for final submission
      const allDeletedFileIds = [
        ...new Set([...deletedFileIds, ...draftDeletedFileIds]),
      ];

      return {
        isValid,
        formId,
        data: collectedData,
        errors: newErrors,
        deletedFileIds: allDeletedFileIds,
      };
    }, [
      disabled,
      subforms,
      formDataBySubform,
      validateAllSubforms,
      collectAllFormData,
      schemas,
      getFilesForField,
      formId,
      deletedFileIds,
      draftDeletedFileIds,
    ]);

    // Validate all forms, apply error state, and scroll to first error if invalid
    const validateAndApplyErrors = useCallback(() => {
      const result = collectAndValidateAllForms();
      setErrors(result.errors);
      if (!result.isValid) {
        setTimeout(() => scrollToFirstError(subforms), 100);
      }
      return result;
    }, [collectAndValidateAllForms, setErrors, scrollToFirstError, subforms]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        setFieldValue: (subformId, fieldId, value) => {
          if (!subformId || !fieldId) return;
          updateFieldValue(subformId, fieldId, value);
        },
        markDeletedFileId: (fileId) => {
          if (!fileId) return;
          addDeletedFileId(fileId);
        },
        unmarkDeletedFileId: (fileId) => {
          if (!fileId) return;
          setDeletedFileIds((prev) => prev.filter((id) => id !== fileId));
        },
        unmarkDeletedFileIds: (fileIds) => {
          if (!fileIds || fileIds.length === 0) return;
          const idsToRemove = new Set(fileIds);
          setDeletedFileIds((prev) =>
            prev.filter((id) => !idsToRemove.has(id))
          );
        },
        validateAndGetData: () => validateAndApplyErrors(),
        getValidationState: () => getValidationState(subforms),
        touchAllFields: () => {
          subforms.forEach((subform) => touchAllSubformFields(subform.id));
        },
      }),
      [
        validateAndApplyErrors,
        getValidationState,
        subforms,
        touchAllSubformFields,
        updateFieldValue,
        addDeletedFileId,
      ]
    );

    // Handle submit all forms
    const handleSubmitAll = useCallback(() => {
      const result = validateAndApplyErrors();
      if (result.isValid && handleSaveForm) {
        handleSaveForm(result.data);
      }
    }, [validateAndApplyErrors, handleSaveForm]);

    // Determine if submit button should be shown
    const shouldShowSubmitButton =
      showSubmitButton !== null ? showSubmitButton : !disabled;

    // Determine if we should show subform based on activeSubformIndex
    const shouldShowSubform = (index) => {
      if (activeSubformIndex === null || activeSubformIndex === undefined) {
        return true;
      }
      return index === activeSubformIndex;
    };

    // Early return AFTER all hooks have been called
    if (subforms.length === 0) {
      return (
        <div className="alert alert-info">
          {t('formRenderer.noFormsAvailable')}
        </div>
      );
    }

    return (
      <div className="formcrafter-form-renderer">
        {subforms.map((subform, index) => {
          const schema =
            schemas[subform.id] ||
            convertToFormCrafterSchema(subform.json_object, subform.id);
          const subformValues = formDataBySubform[subform.id] || {};
          const isVisible = shouldShowSubform(index);
          const subformErrors = errors[subform.id] || {};
          const subformTouched = touchedFields[subform.id] || {};
          const hasErrors = Object.keys(subformErrors).length > 0;

          return (
            <div
              key={`subform-${subform.id}-${filledForm?.id || 'new'}-${index}`}
              className={`formcrafter-subform-container${!isVisible ? ' formcrafter-subform-hidden' : ''}`}
              data-form-id={filledForm?.id}
              data-subform-id={subform.id}
              ref={(el) => {
                containerRefs.current[subform.id] = el;
                registerErrorRef(subform.id, el, hasErrors);
              }}
            >
              {/* IDS Form Renderer - uses apollo-react components */}
              <IDSSubformRenderer
                schema={schema}
                values={subformValues}
                onChange={(fieldId, value) =>
                  handleFieldChange(subform.id, fieldId, value)
                }
                onBlur={(fieldId) => handleFieldBlur(subform.id, fieldId)}
                errors={subformErrors}
                touched={subformTouched}
                disabled={disabled}
                getExistingFiles={(fieldId) =>
                  serverFilesByField[`${String(subform.id)}_${fieldId}`] || []
                }
                onServerFileDelete={handleServerFileDelete}
                // Eager upload props
                formId={formId}
                subformId={subform.id}
                onStagedFileUpload={onStagedFileUpload}
                onStagedFileDelete={onStagedFileDelete}
                eagerUploadEnabled={eagerUploadEnabled}
                draftStagedFiles={draftStagedFiles}
              />
            </div>
          );
        })}

        {shouldShowSubmitButton && handleSaveForm && (
          <div className="formcrafter-submit-container">
            <Button
              color="primary"
              text={t('formRenderer.submitAll')}
              onClick={handleSubmitAll}
            />
          </div>
        )}
      </div>
    );
  }
);

export default FormCrafterFormRenderer;
